import React from 'react';
import _ from 'lodash';

import {
  Button,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  PopoverOrigin
} from '@mui/material';

import { MoreVert } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

export type { PopoverOrigin } from '@mui/material';



const PopoverButton = styled(Button)(() => ({
  padding: 0
}));

const ListItemTypography = styled(Typography, {
  shouldForwardProp: prop =>
    prop !== 'isDangerous'
})<{isDangerous?: boolean;}>(({theme, isDangerous}) => ({
  color: isDangerous ? theme.palette.error.main : undefined
}));

const ListItemContentDiv = styled('div')({
  display: 'flex',
  alignItems: 'center'
});

const IconContainerDiv = styled('div')(({theme}) => ({
  marginRight: theme.spacing(1),
  width: 32,
  height: 32,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const IconPlaceholderDiv = styled('div')({
  width: 32,
  height: 32
});



export interface IPopoverOption {
  id: string;
  label?: string;
  elIcon?: JSX.Element;

  /**
   * If given, overrides `label` and `elIcon`.
   */
  elLabel?: JSX.Element;

  isDangerous?: boolean;

  /**
   * If not given, option is shown by default.
   */
  show?: boolean;
}

export interface IProps {
  options: IPopoverOption[];
  onClickOption: (id: string) => void;
  eatClickEvents?: boolean;
  elButtonIcon?: JSX.Element;

  /**
   * If provided, this will completely override the button element (including `elButtonIcon`).
   */
  fnGenButton?: (handleClick: () => void) => JSX.Element;

  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

export const FC: React.FC<IProps> = props => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  const refPopoverButtonContainer = React.useRef<HTMLDivElement>(null);



  const options = React.useMemo(
    () => {
      const hasAtLeastOneIcon = _.some(props.options, option =>
        !!option.elIcon)

      return hasAtLeastOneIcon ?
        _.map(props.options, option => ({
          ...option,
          elIcon: option.elIcon ?? (
            <IconPlaceholderDiv />
          )
        })) :
        props.options;
    },
    [props.options]
  );



  const optionsToShow = React.useMemo(
    () => _.filter(options, option =>
      _.isNil(option.show) || option.show),
    [options]
  );



  const handleClickButton = React.useCallback(
    (event?: React.MouseEvent) => {
      if (event && props.eatClickEvents) {
        event.stopPropagation();
        event.preventDefault();
      }

      if (optionsToShow.length > 0) {
        setIsPopoverOpen(true);
      }
    },
    [
      setIsPopoverOpen,
      optionsToShow,
      props.eatClickEvents
    ]
  );



  const handleClickOption = React.useCallback(
    (event: React.MouseEvent, id: string) => {
      if (props.eatClickEvents) {
        event.stopPropagation();
        event.preventDefault();
      }

      setIsPopoverOpen(false);
      props.onClickOption(id);
    },
    [
      setIsPopoverOpen,
      props.onClickOption,
      props.eatClickEvents
    ]
  );



  return (
    <>
      <div ref={refPopoverButtonContainer}>
        {props.fnGenButton ? (
          <>
            {props.fnGenButton(handleClickButton)}
          </>

        ) : (
          <PopoverButton
            variant="text"
            size="small"
            onClick={handleClickButton}>
            {props.elButtonIcon || (
              <MoreVert />
            )}
          </PopoverButton>
        )}
      </div>

      <Popover
        open={isPopoverOpen}
        anchorEl={refPopoverButtonContainer.current}
        onClose={() => setIsPopoverOpen(false)}
        anchorOrigin={props.anchorOrigin ?? {
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={props.transformOrigin ?? {
          vertical: 'top',
          horizontal: 'right'
        }}>

        <List disablePadding>
          {_.map(optionsToShow, option => (
            <ListItem
              key={option.id}
              disablePadding>
              <ListItemButton
                dense
                onClick={event => handleClickOption(event, option.id)}>
                {option.elLabel || (
                  <ListItemContentDiv>
                    {option.elIcon && (
                      <IconContainerDiv>
                        {option.elIcon}
                      </IconContainerDiv>
                    )}

                    <ListItemText disableTypography>
                      <ListItemTypography
                        isDangerous={option.isDangerous}>
                        {option.label}
                      </ListItemTypography>
                    </ListItemText>
                  </ListItemContentDiv>
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>

      </Popover>
    </>
  );
};
